'use client';

import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Box, Button } from '@mui/material';
import type { Locale } from '@/config/i18n/locales';
import { setLocaleCookie, buildLocaleSwitchUrl } from '@/config/i18n/localeSwitch';
import { COLORS } from '@/constants';

const SESSION_STORAGE_KEY = 'languageBannerDismissed';
const BANNER_HEIGHT = 48;

export type LanguageBannerMessages = {
  message: string;
  switch: string;
  dismiss: string;
};

type Props = {
  suggestedLocale: Locale | null;
  currentLocale: Locale;
  bannerMessages: LanguageBannerMessages | null;
};

export function LanguageBanner({ suggestedLocale, currentLocale, bannerMessages }: Props) {
  const pathname = usePathname();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (
      suggestedLocale &&
      suggestedLocale !== currentLocale &&
      typeof window !== 'undefined' &&
      !sessionStorage.getItem(SESSION_STORAGE_KEY)
    ) {
      setDismissed(false);
    }
  }, [suggestedLocale, currentLocale]);

  if (!suggestedLocale || !bannerMessages || suggestedLocale === currentLocale || dismissed) {
    return null;
  }

  const handleSwitch = () => {
    setLocaleCookie(suggestedLocale);
    const url = buildLocaleSwitchUrl(pathname ?? window.location.pathname, suggestedLocale);
    window.location.assign(url);
  };

  const handleDismiss = () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(SESSION_STORAGE_KEY, '1');
    }
    setDismissed(true);
  };

  return (
    <>
      <Box component="span" sx={{ display: 'block', height: BANNER_HEIGHT }} aria-hidden />
      <Box
        component="aside"
        role="banner"
        aria-label={bannerMessages.message}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: BANNER_HEIGHT,
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          backgroundColor: 'rgba(245, 245, 245, 0.6)',
          backdropFilter: 'blur(6px)',
          borderBottom: '1px solid rgba(224, 224, 224, 0.9)',
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          fontSize: '0.875rem',
          color: COLORS['neutral/950'],
        }}
      >
        <span>{bannerMessages.message}</span>
        <Button
          size="small"
          variant="contained"
          onClick={handleSwitch}
          sx={{
            backgroundColor: COLORS['primary/700'],
            color: COLORS.white,
            '&:hover': { backgroundColor: COLORS['primary/800'] },
          }}
        >
          {bannerMessages.switch}
        </Button>
        <Button
          size="small"
          variant="text"
          onClick={handleDismiss}
          sx={{
            color: COLORS['neutral/600'],
            '&:hover': { backgroundColor: COLORS['neutral/200'] },
          }}
        >
          {bannerMessages.dismiss}
        </Button>
      </Box>
    </>
  );
}
